import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import CalendlyEmbed from '@/components/CalendlyEmbed';
import { 
  ArrowLeft,
  Sparkles,
  CheckCircle2,
  Workflow,
  Shield,
  Headphones,
  TrendingUp,
  Zap,
  Settings,
  Users,
  BarChart3,
  Calendar,
  FileText,
  Rocket
} from 'lucide-react';

const CustomSolutions: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [meetingScheduled, setMeetingScheduled] = useState(false);

  const handleEventScheduled = () => {
    setMeetingScheduled(true);
  };

  const features = [
    {
      icon: Settings,
      title: t('customSolutions.features.tailored.title'),
      description: t('customSolutions.features.tailored.description')
    },
    {
      icon: Workflow,
      title: t('customSolutions.features.complex.title'),
      description: t('customSolutions.features.complex.description')
    },
    {
      icon: Users,
      title: t('customSolutions.features.dedicated.title'),
      description: t('customSolutions.features.dedicated.description')
    },
    {
      icon: BarChart3,
      title: t('customSolutions.features.analytics.title'),
      description: t('customSolutions.features.analytics.description')
    }
  ];

  // Success State - After scheduling
  if (meetingScheduled) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-brand-dark to-slate-900 flex flex-col">
        <Header />
        
        <div className="flex-1 pt-24 pb-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 sm:p-10 border border-white/20">
              {/* Success Icon */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-brand-secondary/20 mb-6">
                  <CheckCircle2 className="w-10 h-10 text-brand-secondary" />
                </div>
                <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                  {t('customSolutions.success.title')}
                </h1>
                <p className="text-xl text-brand-secondary mb-2">
                  {t('customSolutions.success.subtitle')}
                </p>
              </div>

              {/* Main Content */}
              <div className="space-y-6 text-white/90 leading-relaxed">
                <p className="text-lg text-center">
                  {t('customSolutions.success.thankYou')}
                </p>

                {/* What Happens Next */}
                <div className="mt-8 pt-6 border-t border-white/20">
                  <h3 className="text-2xl font-bold text-white mb-6 text-center">
                    {t('customSolutions.success.nextSteps.title')}
                  </h3>
                  
                  <div className="grid sm:grid-cols-3 gap-6">
                    <div className="bg-white/5 rounded-xl p-6 border border-white/10 text-center">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand-secondary/20 mb-4">
                        <Calendar className="w-6 h-6 text-brand-secondary" />
                      </div>
                      <h4 className="font-bold text-white mb-2">
                        {t('customSolutions.success.nextSteps.step1.title')}
                      </h4>
                      <p className="text-white/70 text-sm">
                        {t('customSolutions.success.nextSteps.step1.description')}
                      </p>
                    </div>

                    <div className="bg-white/5 rounded-xl p-6 border border-white/10 text-center">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand-secondary/20 mb-4">
                        <FileText className="w-6 h-6 text-brand-secondary" />
                      </div>
                      <h4 className="font-bold text-white mb-2">
                        {t('customSolutions.success.nextSteps.step2.title')}
                      </h4>
                      <p className="text-white/70 text-sm">
                        {t('customSolutions.success.nextSteps.step2.description')}
                      </p>
                    </div>

                    <div className="bg-white/5 rounded-xl p-6 border border-white/10 text-center">
                      <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-brand-secondary/20 mb-4">
                        <Rocket className="w-6 h-6 text-brand-secondary" />
                      </div>
                      <h4 className="font-bold text-white mb-2">
                        {t('customSolutions.success.nextSteps.step3.title')}
                      </h4>
                      <p className="text-white/70 text-sm">
                        {t('customSolutions.success.nextSteps.step3.description')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Support Info */}
                <div className="mt-8 pt-6 border-t border-white/20">
                  <div className="bg-brand-secondary/10 rounded-xl p-6 border border-brand-secondary/30 flex items-start gap-4">
                    <Headphones className="w-8 h-8 text-brand-secondary flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-white mb-2">
                        {t('customSolutions.success.support.title')}
                      </h4>
                      <p className="text-white/80">
                        {t('customSolutions.success.support.description')}
                      </p>
                      <p className="text-white/70 mt-3">
                        {t('customSolutions.success.support.contact')}
                        <a 
                          href="mailto:agents@inscius.es" 
                          className="text-brand-secondary font-semibold hover:underline ml-1"
                        >
                          agents@inscius.es
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Return Button */}
              <div className="mt-8 text-center">
                <button
                  onClick={() => navigate('/')}
                  className="px-8 py-3 bg-gradient-to-r from-brand-secondary to-brand-primary text-white rounded-lg font-medium hover:from-brand-primary hover:to-brand-secondary transition-all duration-300 hover:shadow-2xl hover:shadow-brand-secondary/40"
                >
                  {t('customSolutions.success.returnHome')}
                </button>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-brand-dark to-slate-900">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-brand-primary/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-brand-secondary/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-brand-primary/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      {/* Header */}
      <Header />

      <main className="relative pt-28 pb-12">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Link
            to="/solutions"
            className="inline-flex items-center text-brand-secondary hover:text-white transition-colors mb-8 group"
          >
            <ArrowLeft className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform" />
            {t('customSolutions.backToStore')}
          </Link>

          {/* Hero Section */}
          <div className="text-center max-w-4xl mx-auto mb-12">
            {/* Badge */}
            <div className="inline-flex items-center px-4 py-2 bg-brand-primary/20 border border-brand-secondary/50 rounded-full mb-8 backdrop-blur-sm">
              <Sparkles className="w-4 h-4 text-brand-secondary mr-2" />
              <span className="text-brand-secondary text-sm font-medium">{t('customSolutions.hero.badge')}</span>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6 leading-tight">
              <span className="text-white">{t('customSolutions.hero.title')}</span>
            </h1>
            
            <p className="text-xl text-white/70 mb-8 leading-relaxed">
              {t('customSolutions.hero.subtitle')}
            </p>
          </div>

          {/* Pricing Section - Horizontal Cards */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-white text-center mb-6">{t('customSolutions.pricing.title')}</h2>
            
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              {/* Implementation Fee Card */}
              <div className="relative backdrop-blur-lg rounded-2xl border border-brand-secondary/30 bg-gradient-to-br from-brand-secondary/10 to-brand-primary/10 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-brand-secondary/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Zap className="w-7 h-7 text-brand-secondary" />
                  </div>
                  <div>
                    <span className="text-sm text-white/60 block">{t('customSolutions.pricing.implementationLabel')}</span>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-white">{t('customSolutions.pricing.startingAt')}</span>
                      <span className="text-3xl font-bold text-brand-secondary">€1,200</span>
                    </div>
                    <p className="text-white/50 text-sm mt-1">{t('customSolutions.pricing.implementationNote')}</p>
                  </div>
                </div>
              </div>

              {/* Monthly Fee Card */}
              <div className="relative backdrop-blur-lg rounded-2xl border border-brand-secondary/30 bg-gradient-to-br from-brand-primary/10 to-brand-secondary/10 p-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-brand-primary/20 rounded-xl flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-7 h-7 text-brand-secondary" />
                  </div>
                  <div>
                    <span className="text-sm text-white/60 block">{t('customSolutions.pricing.monthlyLabel')}</span>
                    <div className="flex items-baseline gap-2">
                      <span className="text-2xl font-bold text-white">{t('customSolutions.pricing.from')}</span>
                      <span className="text-3xl font-bold text-brand-secondary">€250</span>
                      <span className="text-white/60">{t('customSolutions.pricing.perMonth')}</span>
                    </div>
                    <p className="text-white/50 text-sm mt-1">{t('customSolutions.pricing.monthlyNote')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Why Custom - Horizontal */}
            <div className="relative backdrop-blur-lg rounded-2xl border border-white/10 bg-gradient-to-br from-white/5 to-white/10 p-6">
              <h3 className="text-lg font-semibold text-white mb-4 text-center">{t('customSolutions.whyCustom.title')}</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-brand-secondary flex-shrink-0 mt-0.5" />
                  <span className="text-white/70 text-sm">{t('customSolutions.whyCustom.point1')}</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-brand-secondary flex-shrink-0 mt-0.5" />
                  <span className="text-white/70 text-sm">{t('customSolutions.whyCustom.point2')}</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-brand-secondary flex-shrink-0 mt-0.5" />
                  <span className="text-white/70 text-sm">{t('customSolutions.whyCustom.point3')}</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-brand-secondary flex-shrink-0 mt-0.5" />
                  <span className="text-white/70 text-sm">{t('customSolutions.whyCustom.point4')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Calendly Booking Section - Full Width */}
          <div className="relative backdrop-blur-lg rounded-2xl border border-brand-secondary/30 bg-gradient-to-br from-slate-800/90 to-slate-900/90 p-6 sm:p-8 mb-16">
            <div className="text-center mb-6">
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">{t('customSolutions.booking.title')}</h2>
              <p className="text-white/60">{t('customSolutions.booking.subtitle')}</p>
              
              {/* Trust Badges */}
              <div className="flex items-center justify-center gap-8 mt-4">
                <div className="flex items-center gap-2 text-white/50 text-sm">
                  <Shield className="w-5 h-5" />
                  <span>{t('customSolutions.trust.noObligation')}</span>
                </div>
                <div className="flex items-center gap-2 text-white/50 text-sm">
                  <Headphones className="w-5 h-5" />
                  <span>{t('customSolutions.trust.freeConsultation')}</span>
                </div>
              </div>
            </div>
            
            {/* Calendly Embed - Centered */}
            <div className="max-w-3xl mx-auto rounded-xl overflow-hidden bg-white/5 border border-white/10">
              <CalendlyEmbed 
                url="https://calendly.com/agents-inscius/60min"
                onEventScheduled={handleEventScheduled}
                className="min-h-[580px]"
              />
            </div>
          </div>

          {/* Features Grid */}
          <div className="mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-10">{t('customSolutions.features.title')}</h2>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature, idx) => {
                const IconComponent = feature.icon;
                return (
                  <div
                    key={idx}
                    className="group relative backdrop-blur-lg rounded-xl p-6 border border-white/10 bg-gradient-to-br from-white/5 to-white/10 transition-all duration-300 hover:border-brand-secondary/50 hover:shadow-xl hover:shadow-brand-secondary/20"
                  >
                    <div className="w-12 h-12 bg-gradient-to-br from-brand-primary/20 to-brand-secondary/20 rounded-xl flex items-center justify-center mb-4 group-hover:from-brand-secondary/30 group-hover:to-brand-primary/30 transition-all duration-300">
                      <IconComponent className="w-6 h-6 text-brand-secondary" />
                    </div>
                    <h3 className="text-white font-semibold mb-2">{feature.title}</h3>
                    <p className="text-white/60 text-sm leading-relaxed">{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Process Section */}
          <div className="backdrop-blur-lg rounded-3xl border border-white/10 bg-gradient-to-br from-white/5 to-white/10 p-8 sm:p-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-10">{t('customSolutions.process.title')}</h2>
            
            <div className="grid sm:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-12 h-12 bg-brand-secondary rounded-full flex items-center justify-center text-white font-bold text-lg mx-auto mb-4">1</div>
                <h3 className="text-lg font-semibold text-white mb-2">{t('customSolutions.process.step1.title')}</h3>
                <p className="text-white/60 text-sm">{t('customSolutions.process.step1.description')}</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-brand-secondary rounded-full flex items-center justify-center text-white font-bold text-lg mx-auto mb-4">2</div>
                <h3 className="text-lg font-semibold text-white mb-2">{t('customSolutions.process.step2.title')}</h3>
                <p className="text-white/60 text-sm">{t('customSolutions.process.step2.description')}</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-brand-secondary rounded-full flex items-center justify-center text-white font-bold text-lg mx-auto mb-4">3</div>
                <h3 className="text-lg font-semibold text-white mb-2">{t('customSolutions.process.step3.title')}</h3>
                <p className="text-white/60 text-sm">{t('customSolutions.process.step3.description')}</p>
              </div>
              
              <div className="text-center">
                <div className="w-12 h-12 bg-brand-secondary rounded-full flex items-center justify-center text-white font-bold text-lg mx-auto mb-4">4</div>
                <h3 className="text-lg font-semibold text-white mb-2">{t('customSolutions.process.step4.title')}</h3>
                <p className="text-white/60 text-sm">{t('customSolutions.process.step4.description')}</p>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default CustomSolutions;
